import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Avatar
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import { useExpense } from '../../context/ExpenseContext';
import { usePlayer } from '../../context/PlayerContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import './AddExpenseDialog.scss';

const EXPENSE_CATEGORIES = [
  'General',
  'Food & Drinks',
  'Transportation',
  'Entertainment',
  'Equipment',
  'Tips',
  'House',
  'Other'
];

const AddExpenseDialog = ({ open, onClose }) => {
  const { addExpense } = useExpense();
  const { players } = usePlayer();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('General');
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [error, setError] = useState('');
  
  const MIN_AMOUNT = 1;
  const MAX_AMOUNT = 1000;

  const validateAmount = (value) => {
    const expenseAmount = parseFloat(value);
    
    if (!value || isNaN(expenseAmount) || expenseAmount <= 0) {
      return 'Please enter a valid amount';
    }

    if (expenseAmount < MIN_AMOUNT) {
      return `Minimum amount is $${MIN_AMOUNT}`;
    }

    if (expenseAmount > MAX_AMOUNT) {
      return `Maximum amount is $${MAX_AMOUNT}`;
    }

    return '';
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    const validationError = validateAmount(amount);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }

    const expenseAmount = parseFloat(amount);
    const selectedPlayer = selectedPlayerId ? players.find(p => p.id === selectedPlayerId) : null;
    
    addExpense(
      expenseAmount, 
      description.trim(), 
      category,
      selectedPlayer ? selectedPlayer.id : null,
      selectedPlayer ? selectedPlayer.name : null
    );
    
    setAmount('');
    setDescription('');
    setCategory('General');
    setSelectedPlayerId('');
    setError('');
    onClose();
  };

  const handleClose = () => {
    setAmount('');
    setDescription('');
    setCategory('General');
    setSelectedPlayerId('');
    setError('');
    onClose();
  };

  const handleAmountChange = (event) => {
    const value = event.target.value;
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
      setAmount(value);
      
      if (error && error.includes('amount')) {
        setError('');
      }
      
      if (value && !isNaN(parseFloat(value))) {
        const validationError = validateAmount(value);
        if (validationError) {
          setError(validationError);
        }
      }
    }
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
    if (error && error.includes('description')) {
      setError('');
    }
  };

  const isValidForm = () => {
    const expenseAmount = parseFloat(amount);
    return amount && !isNaN(expenseAmount) && 
           expenseAmount >= MIN_AMOUNT && expenseAmount <= MAX_AMOUNT &&
           description.trim();
  };

  const selectedPlayer = selectedPlayerId ? players.find(p => p.id === selectedPlayerId) : null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      className="add-expense-dialog"
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle className="dialog-title">
          <Box className="title-content">
            <ReceiptIcon className="title-icon" />
            <Typography variant="h5">
              Add Miscellaneous Expense
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent className="dialog-content">
          <Box className="form-fields">
            <Alert severity="info" className="validation-info">
              Amount must be between ${MIN_AMOUNT} and ${MAX_AMOUNT}
            </Alert>
            
            <TextField
              autoFocus
              fullWidth
              label="Amount"
              variant="outlined"
              value={amount}
              onChange={handleAmountChange}
              error={!!error && error.includes('amount')}
              helperText={
                error && error.includes('amount') 
                  ? error 
                  : `Enter amount between $${MIN_AMOUNT} - $${MAX_AMOUNT}`
              }
              placeholder="50.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    $
                  </InputAdornment>
                ),
              }}
              className="amount-input"
            />
            
            <TextField
              fullWidth
              label="Description"
              variant="outlined"
              value={description}
              onChange={handleDescriptionChange}
              error={!!error && error.includes('description')}
              helperText={error && error.includes('description') ? error : 'What was this expense for?'}
              placeholder="Dinner, taxi, equipment, etc."
              className="description-input"
            />
            
            <FormControl fullWidth variant="outlined" className="category-select">
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                label="Category"
              >
                {EXPENSE_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth variant="outlined" className="player-select">
              <InputLabel>Link to Player (Optional)</InputLabel>
              <Select
                value={selectedPlayerId}
                onChange={(e) => setSelectedPlayerId(e.target.value)}
                label="Link to Player (Optional)"
                renderValue={(value) => {
                  if (!value) return 'No player selected';
                  const player = players.find(p => p.id === value);
                  if (!player) return 'No player selected';
                  
                  const playerInitials = getInitials(player.name);
                  const backgroundColor = player.profileColor || "#006aff";
                  const textColor = getContrastTextColor(backgroundColor);
                  
                  return (
                    <Box className="selected-player">
                      <Avatar 
                        className="player-avatar-small"
                        sx={{
                          backgroundColor: backgroundColor,
                          color: textColor,
                          width: 24,
                          height: 24,
                          fontSize: '0.875rem',
                          fontWeight: 700
                        }}
                      >
                        {playerInitials}
                      </Avatar>
                      <span>{player.name}</span>
                    </Box>
                  );
                }}
              >
                <MenuItem value="">
                  <Box className="no-player-option">
                    <PersonIcon className="no-player-icon" />
                    <span>No player (general expense)</span>
                  </Box>
                </MenuItem>
                {players.map((player) => {
                  const playerInitials = getInitials(player.name);
                  const backgroundColor = player.profileColor || "#006aff";
                  const textColor = getContrastTextColor(backgroundColor);
                  
                  return (
                    <MenuItem key={player.id} value={player.id}>
                      <Box className="player-option">
                        <Avatar 
                          className="player-avatar-small"
                          sx={{
                            backgroundColor: backgroundColor,
                            color: textColor,
                            width: 32,
                            height: 32,
                            fontSize: '1rem',
                            fontWeight: 700,
                            marginRight: '12px'
                          }}
                        >
                          {playerInitials}
                        </Avatar>
                        <Box>
                          <Typography variant="body1">{player.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {player.buyInHistory?.length || 0} buy-ins
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            
            {selectedPlayer && (
              <Alert severity="success" className="player-info">
                <Box className="player-expense-info">
                  <Typography variant="body2">
                    This expense will be linked to <strong>{selectedPlayer.name}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You can filter expenses by player later
                  </Typography>
                </Box>
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions className="dialog-actions">
          <Button 
            onClick={handleClose}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={!isValidForm()}
            color="error"
          >
            Add Expense
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddExpenseDialog;